<template>
  <div class="view selfie">
    <div class="selfie__header">
      <p class="selfie__step orbitron">PASUL 1 / 3</p>
      <h2 class="selfie__title orbitron">FACE SCAN</h2>
      <p class="selfie__sub">Poziționează-te în cadru și apasă pentru a face selfie</p>
    </div>

    <div class="selfie__frame-wrap">
      <div class="selfie__frame" :class="{ 'selfie__frame--captured': captured }">
        <div class="corner corner--tl"></div>
        <div class="corner corner--tr"></div>
        <div class="corner corner--bl"></div>
        <div class="corner corner--br"></div>
        <div class="scan-line" v-if="!captured"></div>

        <video
          v-show="!captured"
          ref="videoEl"
          autoplay
          playsinline
          muted
          class="selfie__video"
        />
        <canvas ref="canvasEl" class="selfie__canvas" :class="{ visible: captured }" />

        <div class="selfie__countdown" v-if="counting">
          <span class="orbitron">{{ countdown }}</span>
        </div>

        <div class="selfie__overlay-captured" v-if="captured">
          <span class="orbitron">SCANAT</span>
        </div>
      </div>
    </div>

    <div class="selfie__actions">
      <button v-if="!captured" class="selfie__capture-btn" :disabled="counting" @click="startCountdown">
        <div class="capture-ring"></div>
        <div class="capture-inner"></div>
      </button>

      <div v-else class="selfie__confirm-btns">
        <button class="neon-btn neon-btn--ghost" @click="retake">REFĂ</button>
        <button class="neon-btn" @click="confirm">CONTINUĂ</button>
      </div>
    </div>

    <div class="selfie__error" v-if="cameraError">
      <p>{{ cameraError }}</p>
      <button class="neon-btn" @click="initCamera">ÎNCEARCĂ DIN NOU</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/store/session'

const router = useRouter()
const session = useSessionStore()

const videoEl = ref(null)
const canvasEl = ref(null)
const captured = ref(false)
const counting = ref(false)
const countdown = ref(3)
const cameraError = ref(null)

let stream = null

async function initCamera() {
  cameraError.value = null
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
      audio: false,
    })
    videoEl.value.srcObject = stream
  } catch (e) {
    cameraError.value = 'Camera indisponibilă. Verifică permisiunile.'
  }
}

function startCountdown() {
  if (counting.value) return
  counting.value = true
  countdown.value = 3
  const tick = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(tick)
      capture()
    }
  }, 1000)
}

function capture() {
  const video = videoEl.value
  const canvas = canvasEl.value
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const ctx = canvas.getContext('2d')
  ctx.save()
  ctx.scale(-1, 1)
  ctx.drawImage(video, -canvas.width, 0)
  ctx.restore()
  captured.value = true
  counting.value = false
}

function retake() {
  captured.value = false
  session.selfiePhoto = null
}

function confirm() {
  const canvas = canvasEl.value
  session.selfiePhoto = canvas.toDataURL('image/jpeg', 0.9)
  router.push('/quiz')
}

onMounted(initCamera)
onUnmounted(() => { stream?.getTracks().forEach((t) => t.stop()) })
</script>

<style scoped>
.selfie {
  padding: 2rem 4rem;
  gap: 1.5rem;
}

.selfie__step {
  font-size: 0.8rem;
  letter-spacing: 0.4em;
  color: rgba(139, 92, 246, 0.7);
  margin-bottom: 0.3rem;
}

.selfie__title {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: #fff;
  text-shadow: 0 0 30px rgba(139, 92, 246, 0.6);
}

.selfie__sub {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.05em;
}

.selfie__frame-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selfie__frame {
  position: relative;
  width: min(480px, 55vh);
  aspect-ratio: 3/4;
  overflow: hidden;
  border-radius: 12px;
  transition: box-shadow 0.4s ease;
  box-shadow: 0 0 0 1px rgba(139, 92, 246, 0.3), 0 0 40px rgba(139, 92, 246, 0.2);
}

.selfie__frame--captured {
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.8), 0 0 60px rgba(139, 92, 246, 0.5);
}

.selfie__video,
.selfie__canvas {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform: scaleX(-1);
}

.selfie__canvas { position: absolute; inset: 0; transform: none; }
.selfie__canvas.visible { z-index: 1; }

.corner {
  position: absolute;
  width: 30px;
  height: 30px;
  z-index: 5;
  border-color: rgba(139, 92, 246, 0.9);
  border-style: solid;
}

.corner--tl { top: 8px; left: 8px; border-width: 3px 0 0 3px; }
.corner--tr { top: 8px; right: 8px; border-width: 3px 3px 0 0; }
.corner--bl { bottom: 8px; left: 8px; border-width: 0 0 3px 3px; }
.corner--br { bottom: 8px; right: 8px; border-width: 0 3px 3px 0; }

.scan-line {
  position: absolute;
  left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.8), transparent);
  z-index: 4;
  animation: scanDown 2.5s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(139, 92, 246, 0.5);
}

@keyframes scanDown {
  0% { top: 0%; }
  100% { top: 100%; }
}

.selfie__countdown {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 5, 16, 0.6);
  z-index: 10;
  font-size: 8rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 0 40px rgba(139, 92, 246, 0.9);
  animation: countPulse 1s ease-in-out infinite;
}

@keyframes countPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.selfie__overlay-captured {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 0.8rem;
  background: rgba(139, 92, 246, 0.3);
  text-align: center;
  font-size: 0.9rem;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 0.3em;
  z-index: 10;
}

.selfie__capture-btn {
  position: relative;
  width: 80px;
  height: 80px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.capture-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid rgba(139, 92, 246, 0.8);
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
}

.capture-inner {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(139, 92, 246, 0.3);
  transition: background 0.2s ease, transform 0.2s ease;
}

.selfie__capture-btn:active .capture-inner {
  background: rgba(139, 92, 246, 0.8);
  transform: scale(0.9);
}

.selfie__capture-btn:disabled { opacity: 0.5; }

.selfie__confirm-btns {
  display: flex;
  gap: 1.5rem;
}

.neon-btn--ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.5);
  box-shadow: none;
}

.selfie__error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  background: rgba(5, 5, 16, 0.9);
  font-size: 1.2rem;
  color: rgba(255, 100, 100, 0.8);
  z-index: 20;
}
</style>
